/**
 * Service for managing support tickets via the ColdFusion REST API
 * Base URL: https://apicabaza.creago.be/rest/cabaza/support
 */

import { getAuthHeaders } from './authService';

const API_BASE_URL = `${import.meta.env.VITE_API_BASE_URL}/support`;

/**
 * Helper function to handle API responses
 */
const handleResponse = async (response) => {
    const responseText = await response.text();
    console.log('Support API Response status:', response.status);

    let result;
    try {
        result = JSON.parse(responseText);
    } catch (parseError) {
        console.error('Failed to parse JSON:', parseError);
        throw new Error(`Invalid JSON response: ${responseText}`);
    }

    if (!response.ok) {
        const error = new Error(result.message || `HTTP error! status: ${response.status}`);
        error.status = response.status;
        error.data = result;
        throw error;
    }

    return result;
};

// ==================== USER ENDPOINTS ====================

/**
 * Get user's support tickets
 * @param {number} userId - User ID
 * @param {Object} filters - Optional filters
 * @returns {Promise<Object>} Tickets list
 */
export const getTickets = async (userId, filters = {}) => {
    try {
        const params = new URLSearchParams();
        params.append('userId', userId.toString());

        if (filters.status) params.append('status', filters.status);
        if (filters.priority) params.append('priority', filters.priority);
        if (filters.category) params.append('category', filters.category);
        if (filters.search) params.append('search', filters.search);

        const response = await fetch(`${API_BASE_URL}/tickets?${params}`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        return await handleResponse(response);
    } catch (error) {
        console.error('Error fetching tickets:', error);
        throw error;
    }
};

/**
 * Get a single ticket with messages
 * @param {number} ticketId - Ticket ID
 * @param {number} userId - User ID
 * @returns {Promise<Object>} Ticket data with messages
 */
export const getTicketById = async (ticketId, userId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/tickets/${ticketId}?userId=${userId}`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        return await handleResponse(response);
    } catch (error) {
        console.error('Error fetching ticket:', error);
        throw error;
    }
};

/**
 * Create a new support ticket
 * @param {Object} ticketData - Ticket data
 * @param {number} ticketData.userId - User ID
 * @param {string} ticketData.subject - Ticket subject
 * @param {string} ticketData.description - Ticket description
 * @param {string} ticketData.priority - Priority (low, normal, high, urgent)
 * @param {string} ticketData.category - Category (general, bug, feature, billing, account, other)
 * @returns {Promise<Object>} Created ticket info
 */
export const createTicket = async (ticketData) => {
    try {
        const response = await fetch(`${API_BASE_URL}/tickets`, {
            method: 'POST',
            headers: {
                ...getAuthHeaders(),
                'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify(ticketData)
        });

        return await handleResponse(response);
    } catch (error) {
        console.error('Error creating ticket:', error);
        throw error;
    }
};

/**
 * Add a message to a ticket
 * @param {number} ticketId - Ticket ID
 * @param {number} userId - User ID
 * @param {string} message - Message content
 * @returns {Promise<Object>} Message info
 */
export const addMessage = async (ticketId, userId, message) => {
    try {
        const response = await fetch(`${API_BASE_URL}/tickets/${ticketId}/messages`, {
            method: 'POST',
            headers: {
                ...getAuthHeaders(),
                'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify({ userId, message })
        });

        return await handleResponse(response);
    } catch (error) {
        console.error('Error adding message:', error);
        throw error;
    }
};

/**
 * Close a ticket
 * @param {number} ticketId - Ticket ID
 * @param {number} userId - User ID
 * @returns {Promise<Object>} Result
 */
export const closeTicket = async (ticketId, userId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/tickets/${ticketId}/close?userId=${userId}`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        return await handleResponse(response);
    } catch (error) {
        console.error('Error closing ticket:', error);
        throw error;
    }
};

// ==================== CONSTANTS ====================

export const TICKET_STATUS = {
    OPEN: 'open',
    IN_PROGRESS: 'in_progress',
    WAITING: 'waiting',
    RESOLVED: 'resolved',
    CLOSED: 'closed'
};

export const TICKET_PRIORITY = {
    LOW: 'low',
    NORMAL: 'normal',
    HIGH: 'high',
    URGENT: 'urgent'
};

export const TICKET_CATEGORY = {
    GENERAL: 'general',
    BUG: 'bug',
    FEATURE: 'feature',
    BILLING: 'billing',
    ACCOUNT: 'account',
    OTHER: 'other'
};

// Status colors for UI
export const STATUS_COLORS = {
    open: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-300' },
    in_progress: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300' },
    waiting: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300' },
    resolved: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300' },
    closed: { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-300' }
};

// Priority colors for UI
export const PRIORITY_COLORS = {
    low: { bg: 'bg-gray-100', text: 'text-gray-600' },
    normal: { bg: 'bg-blue-100', text: 'text-blue-600' },
    high: { bg: 'bg-orange-100', text: 'text-orange-600' },
    urgent: { bg: 'bg-red-100', text: 'text-red-600' }
};
